import { useEffect, useState } from 'react'
import { Input } from 'almanac-ui/TextInputField/Input'
import { CaptionsLoadingState } from './CaptionsLoadingState'
import Styles from './CaptionTab.module.scss'
import { usePollAICaptionGeneration } from 'hooks/queries/aiTemplates/usePollAICaptionGeneration'
import { processToolCalls } from './processToolCalls'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { reportError } from 'utils/error'
import { TabPanel } from '../TabPanel/TabPanel'
import { Radio, RadioGroup } from 'components/RadioGroup'
import { Button } from 'blaze-ui/Button'
import { ArrowRefresh2 } from 'blaze-ui/icons'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import cx from 'classnames'

type Props = {
  insertCaption: (caption: string) => void
  handleGenerateMore: () => Promise<void>
}

export const CaptionSelection = observer((props: Props) => {
  const { insertCaption, handleGenerateMore } = props

  const { designStore } = useRootStore()
  const { isMobile } = useResponsiveDesign()
  const captionsStore = designStore?.captionsStore
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [saving, setSaving] = useState(false)
  const [captions, setCaptions] = useState<string[]>(captionsStore?.captions ?? [])
  const [isGenerating, setIsGenerating] = useState(true)

  const onError = (e: unknown) => {
    showToastMessage('There was an error generating your captions. Please try again.', 'error')
    reportError(e)
  }

  useEffect(() => {
    captionsStore?.setSectionTitle('Select a Caption')
  }, [captionsStore])

  usePollAICaptionGeneration(
    {
      id: captionsStore?.requestId,
    },
    {
      onSuccess: async (res) => {
        if (['done', 'failed'].includes(res.status)) {
          try {
            if (res.status === 'done') {
              const newCaptions = processToolCalls(res.data)
              setCaptions(newCaptions)
            } else {
              throw new Error('Server reported a failed caption generation')
            }
          } catch (e) {
            onError(e)
          } finally {
            captionsStore?.completeGeneration()
            setIsGenerating(false)
          }
        }
      },
      onError: (e) => {
        onError(e)
        captionsStore?.completeGeneration()
      },
    },
  )

  const selectedCaption = captions[selectedIndex] ?? ''

  const handleSave = async () => {
    setSaving(true)
    await insertCaption(selectedCaption)
    setSaving(false)
  }

  const generateMore = async () => {
    setIsGenerating(true)
    await handleGenerateMore()
  }

  const renderContent = () => {
    if (isGenerating) {
      return <CaptionsLoadingState />
    }

    return (
      <>
        <RadioGroup
          aria-label="Caption Options"
          value={`${selectedIndex}`}
          className={Styles.captionOptions}
          onChange={(index) => {
            setSelectedIndex(Number(index))
          }}
        >
          {captions?.map((caption, index) => {
            const optionNumber = index + 1
            return (
              <div key={index} className={Styles.captionOption}>
                <div className={Styles.radioButton}>
                  <Radio aria-label={`Option ${optionNumber}`} value={`${index}`}>
                    <div />
                  </Radio>
                </div>
                <div className={Styles.optionRow}>Option {optionNumber}</div>
                <Input
                  isTextarea
                  className={Styles.boldInput}
                  size="lg"
                  name={`Caption ${optionNumber}`}
                  value={caption}
                  onChange={(e) => {
                    captionsStore?.updateCaption(index, e.target.value)
                    const newCaptions = captions.map((c, i) => {
                      return i === index ? e.target.value : c
                    })
                    setCaptions(newCaptions)
                  }}
                  onFocus={() => {
                    setSelectedIndex(Number(index))
                  }}
                  aria-label={`Option ${optionNumber}`}
                  placeholder="Write your own caption..."
                />
              </div>
            )
          })}
        </RadioGroup>

        <Button
          variant="secondary"
          size={isMobile ? 'lg' : 'md'}
          isDisabled={isGenerating}
          onPress={generateMore}
          frontIcon={ArrowRefresh2}
          fullWidth
        >
          Regenerate Captions
        </Button>
      </>
    )
  }

  return (
    <TabPanel.Root>
      <TabPanel.Header title="Captions" onBack={() => captionsStore?.openPanel('generate')} />
      <TabPanel.Body>
        <div className={Styles.selectionContainer}>{renderContent()}</div>
        <div className={cx(Styles.footer, Styles.selectionFooter)}>
          <Button isDisabled={saving || isGenerating} size={isMobile ? 'lg' : 'md'} onPress={handleSave}>
            {saving ? 'Saving...' : 'Insert Caption'}
          </Button>
        </div>
      </TabPanel.Body>
    </TabPanel.Root>
  )
})
